import {
  BaseCheckpointSaver,
  Checkpoint,
  CheckpointMetadata,
  CheckpointTuple,
} from "@langchain/langgraph";
import {
  copyCheckpoint,
  type CheckpointListOptions,
  type CheckpointPendingWrite,
  type PendingWrite,
} from "@langchain/langgraph-checkpoint";
import type { RunnableConfig } from "@langchain/core/runnables";
import type { CloudBase } from "@cloudbase/node-sdk";

/** CloudBase database instance type (from app.database()) */
type CloudBaseDb = ReturnType<CloudBase["database"]>;

export interface CloudBaseSaverConfig {
  /** CloudBase database instance from app.database() */
  db: CloudBaseDb;

  /** User ID for multi-tenant isolation */
  userId: string;

  /** Agent ID to distinguish different agents/graphs (default: "default") */
  agentId?: string;

  /** Collection name for checkpoints (default: "checkpoints") */
  checkpointsCollection?: string;

  /** Collection name for writes (default: "checkpoint_writes") */
  writesCollection?: string;
}

/**
 * CloudBaseSaver - LangGraph checkpoint saver implementation using Tencent CloudBase
 *
 * Storage Strategy:
 * - Uses 2 collections: checkpoints and checkpoint_writes
 * - Data is serialized using BaseCheckpointSaver's serde (base64 encoded)
 * - Multi-tenant isolation via userId field
 */
export class CloudBaseSaver extends BaseCheckpointSaver {
  private db: CloudBaseDb;
  private userId: string;
  private agentId: string;
  private checkpointsCollection: string;
  private writesCollection: string;

  constructor(config: CloudBaseSaverConfig) {
    super();
    this.db = config.db;
    this.userId = config.userId;
    this.agentId = config.agentId ?? "default";
    this.checkpointsCollection = config.checkpointsCollection ?? "checkpoints";
    this.writesCollection = config.writesCollection ?? "checkpoint_writes";
  }

  /**
   * Setup the required collections in CloudBase.
   * Call this once before using the saver. Safe to call multiple times.
   */
  async setup(): Promise<void> {
    const createIfNotExists = async (name: string): Promise<void> => {
      try {
        await this.db.createCollection(name);
      } catch (e) {
        const error = e as { code?: string };
        if (error.code !== "DATABASE_COLLECTION_ALREADY_EXIST") {
          throw e;
        }
      }
    };

    await Promise.all([
      createIfNotExists(this.checkpointsCollection),
      createIfNotExists(this.writesCollection),
    ]);
  }

  /**
   * Serialize data to JSON object using serde.
   * CloudBase is a document database, so we store JSON directly instead of Base64.
   * Binary data (Uint8Array) is not supported - fail early if encountered.
   */
  private async serialize(data: unknown): Promise<unknown> {
    const [type, bytes] = await this.serde.dumpsTyped(data);

    if (type === "bytes") {
      throw new Error(
        "Binary data (Uint8Array) is not supported in CloudBaseSaver. " +
          "CloudBase is a document database that only supports JSON-serializable data."
      );
    }

    // type === "json": bytes is UTF-8 encoded JSON string
    const jsonString = new TextDecoder().decode(bytes);
    return JSON.parse(jsonString);
  }

  /**
   * Deserialize JSON object back to data using serde.
   */
  private async deserialize(data: unknown): Promise<unknown> {
    const jsonString = JSON.stringify(data);
    const bytes = new TextEncoder().encode(jsonString);
    return this.serde.loadsTyped("json", bytes);
  }

  /**
   * Retrieves a checkpoint from CloudBase based on the provided config.
   */
  async getTuple(config: RunnableConfig): Promise<CheckpointTuple | undefined> {
    const {
      thread_id,
      checkpoint_ns = "",
      checkpoint_id,
    } = config.configurable ?? {};

    if (!thread_id) {
      return undefined;
    }

    // Build query
    const query: Record<string, unknown> = {
      _openid: this.userId,
      agent_id: this.agentId,
      thread_id,
      checkpoint_ns,
    };

    if (checkpoint_id) {
      query.checkpoint_id = checkpoint_id;
    }

    // Query checkpoints
    const { data: docs } = await this.db
      .collection(this.checkpointsCollection)
      .where(query)
      .orderBy("checkpoint_id", "desc")
      .limit(1)
      .get();

    if (docs.length === 0) {
      return undefined;
    }

    const doc = docs[0];
    const checkpointId = doc.checkpoint_id as string;

    // Deserialize checkpoint and metadata
    const checkpoint = (await this.deserialize(doc.checkpoint)) as Checkpoint;
    const metadata = (await this.deserialize(
      doc.metadata
    )) as CheckpointMetadata;

    // Query pending writes with pagination (CloudBase max 100 per query)
    const writeDocs: Record<string, unknown>[] = [];
    let writeSkip = 0;
    const writePageSize = 100;

    while (true) {
      const { data: docs } = await this.db
        .collection(this.writesCollection)
        .where({
          _openid: this.userId,
          agent_id: this.agentId,
          thread_id,
          checkpoint_ns,
          checkpoint_id: checkpointId,
        })
        .orderBy("idx", "asc")
        .skip(writeSkip)
        .limit(writePageSize)
        .get();

      writeDocs.push(...docs);
      if (docs.length < writePageSize) break;
      writeSkip += docs.length;
    }

    const pendingWrites: CheckpointPendingWrite[] = await Promise.all(
      writeDocs.map(async (writeDoc) => {
        const value = await this.deserialize(writeDoc.value);
        return [
          writeDoc.task_id as string,
          writeDoc.channel as string,
          value,
        ] as CheckpointPendingWrite;
      })
    );

    return {
      config: {
        configurable: {
          thread_id,
          checkpoint_ns,
          checkpoint_id: checkpointId,
        },
      },
      checkpoint,
      metadata,
      pendingWrites,
      parentConfig: doc.parent_checkpoint_id
        ? {
            configurable: {
              thread_id,
              checkpoint_ns,
              checkpoint_id: doc.parent_checkpoint_id as string,
            },
          }
        : undefined,
    };
  }

  /**
   * List checkpoints from CloudBase, ordered by checkpoint_id descending.
   */
  async *list(
    config: RunnableConfig,
    options?: CheckpointListOptions
  ): AsyncGenerator<CheckpointTuple> {
    const { limit, before, filter } = options ?? {};
    const thread_id = config.configurable?.thread_id;
    const checkpoint_ns = config.configurable?.checkpoint_ns;

    if (!thread_id) {
      throw new Error("Thread ID is required");
    }

    const _ = this.db.command;
    const query: Record<string, unknown> = {
      _openid: this.userId,
      agent_id: this.agentId,
      thread_id,
    };

    // Only filter by checkpoint_ns if explicitly provided
    if (checkpoint_ns !== undefined && checkpoint_ns !== null) {
      query.checkpoint_ns = checkpoint_ns;
    }

    // Apply before filter using lt operator
    if (before?.configurable?.checkpoint_id) {
      query.checkpoint_id = _.lt(before.configurable.checkpoint_id);
    }

    // Apply metadata filters (only valid keys)
    const validCheckpointMetadataKeys = ["source", "step", "writes", "parents"];
    if (filter) {
      for (const [key, value] of Object.entries(filter)) {
        if (value !== undefined && validCheckpointMetadataKeys.includes(key)) {
          query[`metadata.${key}`] = value;
        }
      }
    }

    // CloudBase max 100 records per query
    let skip = 0;
    const pageSize = Math.min(limit ?? 100, 100);
    let yielded = 0;

    while (true) {
      const { data: docs } = await this.db
        .collection(this.checkpointsCollection)
        .where(query)
        .orderBy("checkpoint_id", "desc")
        .skip(skip)
        .limit(pageSize)
        .get();

      if (docs.length === 0) break;

      for (const doc of docs) {
        // Deserialize
        const checkpoint = (await this.deserialize(
          doc.checkpoint
        )) as Checkpoint;
        const metadata = (await this.deserialize(
          doc.metadata
        )) as CheckpointMetadata;

        // Query pending writes for this checkpoint with pagination
        const writeDocs: Record<string, unknown>[] = [];
        let writeSkip = 0;
        const writePageSize = 100;

        while (true) {
          const { data: pageWriteDocs } = await this.db
            .collection(this.writesCollection)
            .where({
              _openid: this.userId,
              agent_id: this.agentId,
              thread_id: doc.thread_id,
              checkpoint_ns: doc.checkpoint_ns,
              checkpoint_id: doc.checkpoint_id,
            })
            .orderBy("idx", "asc")
            .skip(writeSkip)
            .limit(writePageSize)
            .get();

          writeDocs.push(...pageWriteDocs);
          if (pageWriteDocs.length < writePageSize) break;
          writeSkip += pageWriteDocs.length;
        }

        const pendingWrites: CheckpointPendingWrite[] = await Promise.all(
          writeDocs.map(async (writeDoc) => {
            const value = await this.deserialize(writeDoc.value);
            return [
              writeDoc.task_id as string,
              writeDoc.channel as string,
              value,
            ] as CheckpointPendingWrite;
          })
        );

        yield {
          config: {
            configurable: {
              thread_id: doc.thread_id as string,
              checkpoint_ns: doc.checkpoint_ns as string,
              checkpoint_id: doc.checkpoint_id as string,
            },
          },
          checkpoint,
          metadata,
          pendingWrites,
          parentConfig: doc.parent_checkpoint_id
            ? {
                configurable: {
                  thread_id: doc.thread_id as string,
                  checkpoint_ns: doc.checkpoint_ns as string,
                  checkpoint_id: doc.parent_checkpoint_id as string,
                },
              }
            : undefined,
        };

        yielded++;
        if (limit && yielded >= limit) return;
      }

      skip += docs.length;
      if (docs.length < pageSize) break;
    }
  }

  /**
   * Save a checkpoint to CloudBase.
   */
  async put(
    config: RunnableConfig,
    checkpoint: Checkpoint,
    metadata: CheckpointMetadata
  ): Promise<RunnableConfig> {
    if (!config.configurable) {
      throw new Error("Empty configuration supplied.");
    }

    const thread_id = config.configurable.thread_id;
    const checkpoint_ns = config.configurable.checkpoint_ns ?? "";
    const checkpoint_id = checkpoint.id;

    if (!thread_id) {
      throw new Error(
        'Missing "thread_id" field in passed "config.configurable".'
      );
    }

    // Copy checkpoint to avoid mutation during serialization
    const preparedCheckpoint = copyCheckpoint(checkpoint);

    // Serialize checkpoint and metadata to JSON objects
    const serializedCheckpoint = await this.serialize(preparedCheckpoint);
    const serializedMetadata = await this.serialize(metadata);

    const doc = {
      _openid: this.userId,
      agent_id: this.agentId,
      thread_id,
      checkpoint_ns,
      checkpoint_id,
      parent_checkpoint_id: config.configurable.checkpoint_id ?? null,
      checkpoint: serializedCheckpoint,
      metadata: serializedMetadata,
      created_at: new Date(),
    };

    // Use custom _id + set() for INSERT OR REPLACE behavior
    const docId = `${this.agentId}:${thread_id}:${checkpoint_ns}:${checkpoint_id}`;
    await this.db.collection(this.checkpointsCollection).doc(docId).set(doc);

    return {
      configurable: {
        thread_id,
        checkpoint_ns,
        checkpoint_id,
      },
    };
  }

  /**
   * Save intermediate writes to CloudBase.
   */
  async putWrites(
    config: RunnableConfig,
    writes: PendingWrite[],
    taskId: string
  ): Promise<void> {
    if (!config.configurable) {
      throw new Error("Empty configuration supplied.");
    }
    if (!config.configurable.thread_id) {
      throw new Error("Missing thread_id field in config.configurable.");
    }
    if (!config.configurable.checkpoint_id) {
      throw new Error("Missing checkpoint_id field in config.configurable.");
    }

    const thread_id = config.configurable.thread_id;
    const checkpoint_ns = config.configurable.checkpoint_ns ?? "";
    const checkpoint_id = config.configurable.checkpoint_id;

    // Pre-serialize all values before transaction (async operations must be outside)
    const serializedWrites = await Promise.all(
      writes.map(async ([channel, value], idx) => ({
        channel,
        value: await this.serialize(value),
        idx,
        docId: `${this.agentId}:${thread_id}:${checkpoint_ns}:${checkpoint_id}:${taskId}:${idx}`,
      }))
    );

    // Use transaction for atomic writes
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await this.db.runTransaction(async (transaction: any) => {
      for (const { channel, value, idx, docId } of serializedWrites) {
        await transaction.collection(this.writesCollection).doc(docId).set({
          _openid: this.userId,
          agent_id: this.agentId,
          thread_id,
          checkpoint_ns,
          checkpoint_id,
          task_id: taskId,
          idx,
          channel,
          value,
        });
      }
    });
  }

  /**
   * Delete all checkpoints and writes for a thread.
   */
  async deleteThread(threadId: string): Promise<void> {
    // Delete from checkpoints collection
    await this.db
      .collection(this.checkpointsCollection)
      .where({
        _openid: this.userId,
        agent_id: this.agentId,
        thread_id: threadId,
      })
      .remove();

    // Delete from writes collection
    await this.db
      .collection(this.writesCollection)
      .where({
        _openid: this.userId,
        agent_id: this.agentId,
        thread_id: threadId,
      })
      .remove();
  }
}
